import { useState, useEffect, useRef, useCallback } from "react";
const BAR_COUNT = 80;

export default function useAudioProcessor(isListening) {
  const [audioData, setAudioData] = useState(Array(BAR_COUNT).fill(0));
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);

  const startAudioProcessing = useCallback(async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Initialize AudioContext
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Create an analyzer
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.8;
      analyserRef.current = analyser;

      // Connect the microphone to the analyzer
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;

      // Create a data array to store the audio data
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;

      // Start animation
      animateAudio();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      // Fallback to mock visualization
      startMockVisualization();
    }
  }, []);

  const stopAudioProcessing = useCallback(() => {
    // Cancel any running animation frame
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Disconnect audio source
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    // Stop all tracks in the stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Reset audio data
    setAudioData(Array(BAR_COUNT).fill(0));
  }, []);

  // Animation function to update audio data
  const animateAudio = () => {
    if (!analyserRef.current || !dataArrayRef.current) return;

    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Process the audio data
    const average =
      Array.from(dataArrayRef.current)
        .slice(0, 100)
        .reduce((sum, value) => sum + value, 0) / 100;

    // Determine if there's voice based on amplitude threshold
    const hasVoice = average > 10;

    // Generate visualization data
    let newData;
    if (hasVoice) {
      // Create wave pattern for voice
      newData = Array(BAR_COUNT)
        .fill(0)
        .map((_, i) => {
          const value = dataArrayRef.current[i * 4] / 10; // Scale down for visualization
          return value;
        });
    } else {
      // Create dot pattern for silence
      newData = Array(BAR_COUNT)
        .fill(0)
        .map((_, i) => (i % 5 === 0 ? 8 : 0));
    }

    setAudioData(newData);
    animationRef.current = requestAnimationFrame(animateAudio);
  };

  // Mock visualization for fallback
  const startMockVisualization = () => {
    const mockInterval = setInterval(() => {
      const mockData = Array(BAR_COUNT)
        .fill(0)
        .map(() => Math.random() * 30);
      setAudioData(mockData);
    }, 100);

    // Store the interval ID in the ref for cleanup
    animationRef.current = mockInterval;
  };

  // Initialize audio processing
  useEffect(() => {
    // Start audio processing when listening begins
    if (isListening) {
      startAudioProcessing();
    }
    // Clean up when listening stops
    else {
      stopAudioProcessing();
    }

    return () => {
      stopAudioProcessing();
    };
  }, [isListening, startAudioProcessing, stopAudioProcessing]);

  return audioData;
}
